import React, { useState, useEffect, useRef } from 'react'
import { useDropzone } from 'react-dropzone'
import Swal from 'sweetalert2'
import withReactContent from 'sweetalert2-react-content'
import * as pdfjsLib from 'pdfjs-dist/legacy/build/pdf.js'
import {
  REPORT_IMAGE_MAX_SIZE,
  REPORT_PDF_MAX_SIZE,
  buildPdfResourceItem,
  getAttachmentFileUrl,
  getAttachmentId,
  uploadReportStaticFile,
} from '../resourceUpload'
import { getReportImageSource } from '../reportBind.util'

const PDF_PREVIEW_RENDER_SCALE = 2.5
const PDF_PREVIEW_MAX_PIXELS = 14000000

pdfjsLib.GlobalWorkerOptions.workerSrc =
  '/his/libs/pdfjs/2.10.377/pdf.worker.min.js'

const isSmallPdfBox = (item = {}) => {
  const isPixelSize = (value) =>
    typeof value === 'number' ||
    /^[0-9.]+(px)?$/i.test(String(value || '').trim())

  if (!isPixelSize(item.width) || !isPixelSize(item.height)) {
    return false
  }

  const width = parseFloat(item.width)
  const height = parseFloat(item.height)

  return (
    Number.isFinite(width) &&
    Number.isFinite(height) &&
    width <= 360 &&
    height <= 160
  )
}

const isFullPagePdfCandidate = (item = {}) =>
  item.fullPage ||
  item.pdfPreviewMode === 'page' ||
  item.width === '100%' ||
  item.height === '100%' ||
  isSmallPdfBox(item)

const PdfCanvasPreview = ({ source, fileName, width, height }) => {
  const canvasRef = useRef(null)
  const [status, setStatus] = useState('loading')

  useEffect(() => {
    if (!source) return undefined

    let cancelled = false
    let loadingTask = null
    let renderTask = null

    const renderPdfPage = async () => {
      setStatus('loading')

      try {
        const response = await fetch(source, { credentials: 'include' })
        if (!response.ok) {
          throw new Error('Cannot load PDF preview')
        }

        const typedArray = new Uint8Array(await response.arrayBuffer())
        loadingTask = pdfjsLib.getDocument({ data: typedArray })
        const pdf = await loadingTask.promise
        const page = await pdf.getPage(1)
        const canvas = canvasRef.current
        const parent = canvas?.parentElement

        if (!canvas || !parent || cancelled) return

        const viewport = page.getViewport({ scale: 1 })
        const parentWidth = parent.clientWidth || viewport.width
        const parentHeight = parent.clientHeight || viewport.height
        const xScale = parentWidth / viewport.width
        const yScale = parentHeight / viewport.height
        const deviceScale = window.devicePixelRatio || 1
        const requestedScale = deviceScale * PDF_PREVIEW_RENDER_SCALE
        const requestedPixels =
          parentWidth * parentHeight * requestedScale * requestedScale
        const pixelLimitScale =
          requestedPixels > PDF_PREVIEW_MAX_PIXELS
            ? Math.sqrt(PDF_PREVIEW_MAX_PIXELS / (parentWidth * parentHeight))
            : requestedScale
        const outputScale = Math.max(deviceScale, pixelLimitScale)
        const context = canvas.getContext('2d')

        canvas.width = Math.floor(parentWidth * outputScale)
        canvas.height = Math.floor(parentHeight * outputScale)
        canvas.style.width = '100%'
        canvas.style.height = '100%'
        context.imageSmoothingEnabled = false

        renderTask = page.render({
          canvasContext: context,
          viewport,
          transform: [
            xScale * outputScale,
            0,
            0,
            yScale * outputScale,
            0,
            0,
          ],
        })
        await renderTask.promise

        if (!cancelled) {
          setStatus('ready')
        }
      } catch (error) {
        if (!cancelled) {
          console.error('PDF preview error', error)
          setStatus('error')
        }
      }
    }

    renderPdfPage()

    return () => {
      cancelled = true
      if (renderTask?.cancel) {
        renderTask.cancel()
      }
      if (loadingTask?.destroy) {
        loadingTask.destroy()
      }
    }
  }, [source, width, height])

  return (
    <div className="report-pdf-resource__canvas-wrap">
      <canvas ref={canvasRef} aria-label={fileName} />
      {status !== 'ready' && (
        <div className="report-pdf-resource__status">
          {status === 'loading' ? 'กำลังโหลดตัวอย่าง PDF...' : 'ไม่สามารถแสดงตัวอย่าง PDF ได้'}
        </div>
      )}
    </div>
  )
}

export const DropzoneAccepted = (props) => {
  const { item } = props
  const isPdf = item?.type === 'pdf'
  const existingPdfAttachmentId =
    getAttachmentId(item) || getAttachmentId(item?.pdf)
  //console.log('props.item',item)
  const propsFiles = item.data
    ? Object.assign({}, item.data, {
        // width: item.width * 1,
        // height: item.height * 1,
      })
    : null
  //console.log('props',propsFiles[0])
  const [file, setFile] = useState(propsFiles)
  const [isUploadingPdf, setIsUploadingPdf] = useState(false)

  useEffect(() => {
    if (
      isPdf &&
      existingPdfAttachmentId &&
      isFullPagePdfCandidate(item) &&
      (item.left !== 0 ||
        item.top !== 0 ||
        item.width !== '100%' ||
        item.height !== '100%' ||
        !item.fullPage ||
        item.pdfPreviewMode !== 'page') &&
      props.onChange
    ) {
      props.onChange({
        ...item,
        left: 0,
        top: 0,
        width: '100%',
        height: '100%',
        fullPage: true,
        pdfPreviewMode: 'page',
      })
    }
  }, [isPdf, existingPdfAttachmentId, item?.id, item?.width, item?.height])

  const toBase64 = (file) =>
    new Promise((resolve, reject) => {
      const reader = new FileReader()
      reader.readAsDataURL(file)
      reader.onload = () => resolve(reader.result)
      reader.onerror = (error) => reject(error)
    })

  const {
    acceptedFiles,
    // rejectedFiles,
    getRootProps,
    getInputProps,
  } = useDropzone({
    accept: isPdf ? 'application/pdf' : 'image/jpeg, image/png',
    disabled: props.disabled || (isPdf && isUploadingPdf),
    noClick: isPdf,
    onDrop: async (acceptedFiles, rejected) => {
      console.log('acceptedFiles', acceptedFiles)
      if (acceptedFiles.length > 0) {
        const newFiles = acceptedFiles.map((file) =>
          Object.assign(file, {
            preview: URL.createObjectURL(file),
            // width: 128,
            // height: 128,
          })
        )

        setFile(newFiles[0])
        if (props.onChange) {
          if (isPdf) {
            setIsUploadingPdf(true)
            try {
              const attachment = await uploadReportStaticFile(
                newFiles[0],
                'report.pdf',
              )
              props.onChange(
                buildPdfResourceItem(item, attachment, newFiles[0].name),
              )
            } catch (error) {
              withReactContent(Swal).fire({
                icon: 'error',
                title: 'อัปโหลด PDF ไม่สำเร็จ',
                text:
                  error?.response?.data?.message ||
                  error?.message ||
                  'ไม่สามารถอัปโหลดไฟล์ PDF ได้',
              })
            } finally {
              setIsUploadingPdf(false)
            }
          } else {
            props.onChange({
              ...item,
              data: newFiles[0],
              image: await toBase64(newFiles[0]),
            })
          }
        }
      }

      if (rejected.length > 0) {
        const maxSize = isPdf ? REPORT_PDF_MAX_SIZE : REPORT_IMAGE_MAX_SIZE
        if (rejected[0].size > maxSize) {
          if (props.onChange) {
            props.onChange(
              isPdf
                ? { ...item, data: null, pdf: null, attachmentId: '', value: '' }
                : { ...item, image: '' },
            )
          }
        }

        withReactContent(Swal).fire({
          icon: 'warning',
          title: isPdf ? 'เลือก PDF ไม่สำเร็จ!' : 'เลือกรูปภาพไม่สำเร็จ!',
          text: isPdf
            ? 'รองรับเฉพาะไฟล์ PDF และขนาดไม่เกิน 10 MB.'
            : 'เนื่องจากรูปภาพมีขนาดเกินกว่า 2 MB.',
        })
      }
    },
    maxSize: isPdf ? REPORT_PDF_MAX_SIZE : REPORT_IMAGE_MAX_SIZE,
    init: function () {
      console.log('init')
      this.on('error', function (file, message) {
        alert(message)
      })
    },
  })

  const getPreviewSize = () => {
    const width = item?.width || '240px'
    const height = item?.height && item.height !== 'auto' ? item.height : '160px'

    return { width, height }
  }

  const getPdfAttachmentId = () => existingPdfAttachmentId

  const getPdfFileName = () =>
    item?.data?.name ||
    item?.pdf?.originalFileName ||
    item?.pdf?.titleName ||
    item?.pdf?.name ||
    item?.pdf?.fileName ||
    file?.name ||
    item?.value ||
    'PDF file'

  const getPdfSource = () =>
    item?.previewUrl ||
    item?.pdf?.previewUrl ||
    getAttachmentFileUrl(getPdfAttachmentId()) ||
    item?.data?.preview ||
    file?.preview ||
    ''

  const renderPdfPreview = () => {
    const pdfSource = getPdfSource()
    const hasPdf = item?.data || pdfSource || getPdfAttachmentId() || isUploadingPdf

    if (pdfSource && !isUploadingPdf) {
      return (
        <div className="report-pdf-resource has-preview">
          <PdfCanvasPreview
            source={pdfSource}
            fileName={getPdfFileName()}
            width={item?.width}
            height={item?.height}
          />
          <div className="report-pdf-resource__badge">PDF</div>
        </div>
      )
    }

    return (
      <div
        className={`report-pdf-resource ${hasPdf ? 'has-file' : 'is-empty'} ${
          isUploadingPdf ? 'is-uploading' : ''
        }`}
      >
        <div className="report-pdf-resource__icon">
          <i className="fas fa-file-pdf" />
        </div>
        <div className="report-pdf-resource__body">
          <div className="report-pdf-resource__title">
            {isUploadingPdf
              ? 'Uploading PDF'
              : hasPdf
                ? 'PDF attached'
                : 'Drop PDF here'}
          </div>
          <div
            className="report-pdf-resource__filename"
            title={getPdfFileName()}
          >
            {isUploadingPdf
              ? 'Please wait...'
              : hasPdf
                ? getPdfFileName()
                : 'or click to upload'}
          </div>
        </div>
      </div>
    )
  }

  const renderImagePlaceholder = () => {
    if (props.disabled) return null

    const { width, height } = getPreviewSize()

    return (
      <div
        style={{
          width,
          height,
          minWidth: '120px',
          minHeight: '90px',
          border: '1px dashed #9aa0a6',
          background:
            'linear-gradient(135deg, #f8f9fa 25%, #eef1f4 25%, #eef1f4 50%, #f8f9fa 50%, #f8f9fa 75%, #eef1f4 75%, #eef1f4 100%)',
          backgroundSize: '16px 16px',
          color: '#5f6368',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          textAlign: 'center',
          fontSize: '12px',
          lineHeight: 1.3,
          boxSizing: 'border-box',
          padding: '8px',
        }}
      >
        <div>
          <i className="fas fa-image" style={{ display: 'block', fontSize: 20, marginBottom: 6 }} />
          <div>Image preview</div>
          <div>
            {width} x {height}
          </div>
        </div>
      </div>
    )
  }

  const thumbs = () => {
    const imageSource = getReportImageSource(item)
    const isImageBackground = !isPdf && item?.isBackground === true
    const backgroundFillStyle = isImageBackground
      ? {
          width: '100%',
          height: '100%',
          minHeight: '100%',
          display: 'block',
          margin: 0,
          padding: 0,
        }
      : {}

    return (
      <div className="dz-thumb" style={backgroundFillStyle}>
        <div className="dz-thumb-inner" style={backgroundFillStyle}>
          {isPdf ? (
            renderPdfPreview()
          ) : imageSource ? (
            <img
              src={imageSource}
              className="dz-img"
              style={{
                width: item?.isBackground ? '100%' : item?.width,
                height: item?.isBackground ? '100%' : item?.height,
                opacity: item?.opacity,
                objectFit: item?.objectFit || (item?.isBackground ? 'contain' : undefined),
                display: item?.isBackground ? 'block' : undefined,
              }}
            />
          ) : (
            renderImagePlaceholder()
          )}
        </div>
      </div>
    )
  }

  // const imageUploaded = () => {
  //   if (item?.image) {
  //     return (
  //       <div className="dz-thumb" key={item?.image?.fileId}>
  //         <div className="dz-thumb-inner">
  //           <img
  //             src={`${getBaseURL()}/media/attachment/original/${
  //               item?.image?.categoryId
  //             }/images/${item?.image?.name}`}
  //             className="dz-img"
  //             style={{
  //               width: item?.width,
  //               height: item?.height,
  //               opacity: item?.opacity,
  //             }}
  //           />
  //         </div>
  //       </div>
  //     )
  //   }
  // }

  useEffect(
    () => () => {
      // Make sure to revoke the data uris to avoid memory leaks
      //files.forEach(file => URL.revokeObjectURL(file.preview))
    },
    [file]
  )

  const acceptedFilesItems = acceptedFiles.map((file) => (
    <aside className="thumb-container mb-1">{thumbs}</aside>
  ))

  // const rejectedFilesItems = rejectedFiles.map(file => (
  //   <div key={file.path}>
  //     {file.path} - {file.size} bytes
  //   </div>
  // ))

  const isImageBackground = !isPdf && item?.isBackground === true
  const backgroundFillStyle = isImageBackground
    ? {
        width: '100%',
        height: '100%',
        minHeight: '100%',
        display: 'block',
        margin: 0,
        padding: 0,
      }
    : undefined

  const pdfFileInputStyle =
    isPdf &&
    !existingPdfAttachmentId &&
    !props.disabled &&
    !isUploadingPdf
      ? {
          display: 'block',
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          opacity: 0,
          cursor: 'pointer',
          zIndex: 2,
        }
      : null
  const dropzoneStyle = isPdf
    ? { ...(backgroundFillStyle || {}), position: 'relative' }
    : backgroundFillStyle

  return (
    <section
      className={isPdf ? 'report-pdf-dropzone-wrap' : 'pb-1-'}
      style={backgroundFillStyle}
    >
      <div {...getRootProps({ className: 'dropzone', style: dropzoneStyle })}>
        <input
          {...getInputProps()}
          {...(pdfFileInputStyle ? { style: pdfFileInputStyle } : {})}
          aria-label={isPdf ? 'เลือก PDF template' : 'เลือกรูปภาพ'}
        />
        <aside
          className={isPdf ? 'report-pdf-thumb-container' : undefined}
          style={backgroundFillStyle}
        >
          {thumbs()}
        </aside>
      </div>
    </section>
  )
}

class DropzoneAccept extends React.Component {
  render() {
    return <DropzoneAccepted />
  }
}

export default DropzoneAccept
